import { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, Wand2 } from 'lucide-react';
import { Button, Modal, Spinner, Textarea, cx } from '@lp/portal-ui';
import { AI_COPY_TONE_LABELS, AI_IMAGE_SHAPES, type AiCopyTone, type AiImageResult, type AiImageShape, type AiPromptResult, type MediaRef, type PortalAiStatus } from '@lp/shared';
import { api } from '../api.ts';
import { errorMessage } from '../lib/errors.ts';
import { useLoad } from '../lib/useLoad.ts';

const SHAPE_RATIO: Record<AiImageShape, number> = {
  '1:1': 1,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
  '9:16': 9 / 16,
};
const SHAPE_LABEL: Record<AiImageShape, string> = {
  '1:1': '正方 1:1',
  '4:3': '橫向 4:3',
  '3:4': '直向 3:4',
  '4:5': '直向 4:5',
  '16:9': '闊橫 16:9',
  '9:16': '直身 9:16',
};
/** Size the picture comes at, where the use asks for one (LPSS-27: 品牌故事圖片 at least 1200 × 1500). */
const SHAPE_NOTE: Partial<Record<AiImageShape, string>> = { '4:5': '最少 1200 × 1500 像素' };

/** The shape closest to an upload box's Tailwind aspect class (aspect-square, aspect-video, aspect-[4/5] …). */
export function shapeForAspect(aspect: string | undefined): AiImageShape {
  const m = /aspect-\[(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)\]/.exec(aspect ?? '');
  const ratio = aspect?.includes('aspect-square') ? 1 : m ? Number(m[1]) / Number(m[2]) : 16 / 9;
  return AI_IMAGE_SHAPES.reduce((best, s) => (Math.abs(Math.log(SHAPE_RATIO[s] / ratio)) < Math.abs(Math.log(SHAPE_RATIO[best] / ratio)) ? s : best));
}

const usageLine = (used: number, limit: number | null) => (limit === null ? `本月已生成 ${used} 張` : `本月已生成 ${used} / ${limit} 張`);

/** Content the picture should follow, e.g. a 推廣活動's name, description and gifts with the tone of its AI copy. */
export interface AiImageSeed {
  /** At most 400 characters (an image idea's limit). */
  idea: string;
  tone: AiCopyTone | null;
}

/**
 * 「AI 生成圖片」: describe the picture (or let the text model flesh out a short idea), pick the shape, generate,
 * then use it in the upload box. With a `seed`, the description is written from it when the dialog opens (again
 * when the content has changed since). Each picture counts against the plan's monthly AI images.
 */
export function AiImageDialog({
  open,
  onClose,
  shape: suggested,
  purpose,
  seed,
  onUse,
}: {
  open: boolean;
  onClose: () => void;
  shape: AiImageShape;
  purpose: string;
  seed?: AiImageSeed;
  onUse: (ref: MediaRef) => void;
}) {
  const status = useLoad(() => (open ? api.get<PortalAiStatus>('/ai') : Promise.resolve(null)), [open]);
  const [prompt, setPrompt] = useState('');
  const [shape, setShape] = useState<AiImageShape>(suggested);
  const [busy, setBusy] = useState<'prompt' | 'image' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AiImageResult | null>(null);
  // The seed idea the description was last written from (a ref: the effect must not run twice for one opening).
  const seededFrom = useRef<string | null>(null);
  const s = status.data;
  const used = result?.imagesThisMonth ?? s?.imagesThisMonth ?? 0;
  const limit = result ? result.imagesLimit : (s?.imagesLimit ?? null);
  const full = limit !== null && used >= limit;
  const seedIdea = seed?.idea;

  useEffect(() => {
    if (!open || !seedIdea || !s?.text || seededFrom.current === seedIdea) return;
    seededFrom.current = seedIdea;
    void improve(seedIdea);
    // improve reads the latest seed tone itself; only a new opening or new content should start it.
  }, [open, seedIdea, s?.text]);

  function close() {
    if (busy) return;
    setResult(null);
    setError(null);
    onClose();
  }

  async function improve(idea = prompt.trim()) {
    setBusy('prompt');
    setError(null);
    try {
      setPrompt(
        (
          await api.post<AiPromptResult>('/ai/prompt', {
            idea: idea.slice(0, 400),
            purpose,
            tone: seed?.tone ?? undefined,
          })
        ).prompt,
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function generate() {
    setBusy('image');
    setError(null);
    try {
      setResult(
        await api.post<AiImageResult>('/ai/images', {
          prompt: prompt.trim(),
          shape,
          purpose,
        }),
      );
    } catch (e) {
      setError(
        errorMessage(e, {
          LIMIT_REACHED: '本月的 AI 圖片已用完，下月會重新計算；如需更多，請聯絡 LoyaltyPlus。',
        }),
      );
    } finally {
      setBusy(null);
    }
  }

  const ready = prompt.trim().length >= 2;
  return (
    <Modal
      open={open}
      onClose={close}
      wide
      title={
        <span className="inline-flex items-center gap-2">
          <Sparkles className="size-5 text-brand-700" aria-hidden />
          AI 生成圖片
        </span>
      }
      footer={
        s?.images ? (
          result ? (
            <>
              <Button variant="secondary" icon={<RefreshCw className="size-4" />} loading={busy === 'image'} disabled={full || !ready} onClick={() => void generate()}>
                再生成一張
              </Button>
              <Button
                onClick={() => {
                  onUse({ key: result.key, url: result.url });
                  close();
                }}
              >
                使用這張圖片
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={close} disabled={!!busy}>
                取消
              </Button>
              <Button icon={<Sparkles className="size-4" />} loading={busy === 'image'} disabled={full || !ready || busy === 'prompt'} onClick={() => void generate()}>
                生成圖片
              </Button>
            </>
          )
        ) : undefined
      }
    >
      {status.loading && !s ? (
        <Spinner label="載入中…" />
      ) : status.error ? (
        <p className="text-sm text-coral">{errorMessage(status.error)}</p>
      ) : !s?.images ? (
        <p className="text-[15px] text-ink-soft">AI 生成圖片暫時未開放，請稍後再試或聯絡 LoyaltyPlus。</p>
      ) : (
        <div className="space-y-5">
          <div>
            <label htmlFor="ai-prompt" className="mb-1.5 block text-sm font-medium text-ink">
              描述你想要的圖片
            </label>
            {seed && s.text && (busy === 'prompt' || prompt) && (
              <p className="mb-2 flex items-center gap-1.5 text-sm text-ink-soft">
                {busy === 'prompt' && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {busy === 'prompt' ? '正在按' : '已按'}推廣內容{seed.tone ? `及「${AI_COPY_TONE_LABELS[seed.tone]}」語氣` : ''}撰寫圖片描述{busy === 'prompt' ? '…' : '，可修改後才生成圖片。'}
              </p>
            )}
            <Textarea
              id="ai-prompt"
              rows={4}
              maxLength={800}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="例如：秋季新品南瓜拿鐵放在木桌上，旁邊有楓葉，暖色調，簡約商業攝影"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted">寫得越具體，效果越好：主體、場景、顏色、風格。用途：{purpose}</p>
              {s.text && (
                <Button size="sm" variant="secondary" icon={<Wand2 className="size-4" />} loading={busy === 'prompt'} disabled={!ready || busy === 'image'} onClick={() => void improve()}>
                  AI 優化描述
                </Button>
              )}
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-sm font-medium text-ink">圖片比例</div>
            <div role="radiogroup" aria-label="圖片比例" className="flex flex-wrap gap-2">
              {AI_IMAGE_SHAPES.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={shape === k}
                  onClick={() => setShape(k)}
                  className={cx('h-9 rounded-full px-3.5 text-sm transition-colors', shape === k ? 'bg-ink font-medium text-white' : 'bg-white text-ink ring-1 ring-inset ring-ink/20 hover:bg-mint')}
                >
                  {SHAPE_LABEL[k]}
                  {SHAPE_NOTE[k] && <span className={cx('ml-1 text-xs', shape === k ? 'text-white/70' : 'text-muted')}>{SHAPE_NOTE[k]}</span>}
                  {k === suggested &&<span className={cx('ml-1 text-xs', shape === k ? 'text-white/70' : 'text-muted')}>（建議）</span>}
                </button>
              ))}
            </div>
          </div>

          {busy === 'image' && (
            <div className="flex items-center gap-3 rounded-xl bg-canvas p-4 text-sm text-ink-soft ring-1 ring-line">
              <Loader2 className="size-5 animate-spin" aria-hidden />
              生成中，可能需要 1 分鐘以上，請耐心等候。請勿關閉視窗。
            </div>
          )}
          {result && busy !== 'image' && (
            <div className="overflow-hidden rounded-xl bg-canvas ring-1 ring-line">
              <img src={result.url} alt="AI 生成的圖片" className="mx-auto max-h-[46vh] w-auto object-contain" />
            </div>
          )}
          {error && <p className="text-sm text-coral">{error}</p>}
          <p className={cx('text-xs', full ? 'font-medium text-coral' : 'text-muted')}>
            {usageLine(used, limit)}
            {full ? '，本月已用完。' : '。每生成一張計算一次；未使用的圖片不會退回額度。'}
          </p>
        </div>
      )}
    </Modal>
  );
}

/** The 「AI 生成圖片」 link under an upload box; shown only when the brand has the add-on. */
export function AiImageButton({ aspect, purpose, seed, onUse }: { aspect?: string; purpose: string; seed?: AiImageSeed; onUse: (ref: MediaRef) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline">
        <Sparkles className="size-4" aria-hidden />
        AI 生成圖片
      </button>
      <AiImageDialog open={open} onClose={() => setOpen(false)} shape={shapeForAspect(aspect)} purpose={purpose} seed={seed} onUse={onUse} />
    </>
  );
}
